<!-- Copyright: Seán I. O'Donoghue -->
<!-- TODO: refactor to SecondaryItem.vue -->

<template>
  <!-- the year (e.g. 2026, the About page) on the left; the other pages wrap in their own column,
       so every line starts at the same edge -->
  <nav v-if="route.params.year" id="NavBar" :class="{hidden: !showNavBar}">
    <router-link
      v-for="page in pageNames.slice(0, 1)"
      :key="page"
      :to="getPagePath(page)"
      :class="{page: true, year: true, selected: currentYearPages[page].selected}">
      {{ getPageLabel(page) }}
    </router-link>
    <div class="pages">
      <router-link
        v-for="page in pageNames.slice(1)"
        :key="page"
        :to="getPagePath(page)"
        :class="{page: true, selected: currentYearPages[page].selected}">
        {{ getPageLabel(page) }}
      </router-link>
    </div>
  </nav>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(msg); // eslint-disable-line
import {ref, reactive, computed, watch, onMounted} from "vue";
import {useRoute} from "vue-router";
import stringify from "json-stringify-safe";
import {useEventListener} from "@vueuse/core";
const route = useRoute();
const showNavBar = ref(true);
const lastScrollPosition = ref(0);
const currentYearPages = reactive({});
const pageNames = computed(() => Object.keys(currentYearPages));

function getPagePath(page) {
  return `/${route.params.year}/${page.replace("About", "")}`;
}

function getPageLabel(page) {
  return page.replace("About", route.params.year);
}
const allPossiblePages = [
  "About",
  "People",
  "Program",
  "Posters",
  "Photos",
  "Masterclass",
  "VR",
  "Workshop",
  "Showcase",
  "Registration",
  "Venue",
  "Platform",
  "Sponsors",
  "Contact"
];

function pingFile(page) {
  const year = route.params.year;
  if (page === "About") {
    page = "Index.vue";
  } else {
    page = `${page}/Index.vue`;
  }
  try {
    const indexPage = require(`@/assets/years/${year}/${page}`);
    log(`Found @/assets/years/${year}/${page}`);
    log(`indexPage = ${stringify(indexPage)}`);
    if (window.location.host === "vizbi.org") {
      if (indexPage.default.props && indexPage.default.props.devOnly) {
        log(`devOnly = ${indexPage.default.props.devOnly}`);
        return false;
      }
    }
    return true;
  } catch (error) {
    log(`Did not find @/assets/years/${year}/${page}`);
    return false;
  }
}

function updatePageList(year) {
  if (!pingFile("About")) return false;
  let output = allPossiblePages;
  try {
    log(`Checking pages for ${year}`);
    output = output.filter((page) => pingFile(page));
    output = output.reduce((acc, page) => {
      acc[page] = {selected: false};
      return acc;
    }, {});
    const pageNames = Object.keys(output).toString();
    log(`Found vue files for: ${pageNames}`);
    return output;
  } catch (error) {
    console.error(`Did not find file: ${error}`);
  }
}

const pageCache = (() => {
  const cachedPages = {};
  return function (year) {
    if (!year) return;
    if (!cachedPages[year]) cachedPages[year] = updatePageList(year);
    return cachedPages[year];
  };
})();

function updateSelectionState() {
  const routeName = route.name;
  for (const page of Object.keys(currentYearPages)) {
    if (page === "About" && routeName === "AboutYear") {
      currentYearPages[page].selected = true;
    } else if (page === "Posters" && routeName === "Poster") {
      currentYearPages[page].selected = true;
    } else if (page === "Photos" && routeName === "Photos_Year") {
      currentYearPages[page].selected = true;
    } else if (page === routeName) {
      currentYearPages[page].selected = true;
    } else {
      currentYearPages[page].selected = false;
    }
  }
}

function onScroll() {
  const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
  if (currentScroll < 0) return;
  if (currentScroll < 250) {
    showNavBar.value = true;
    return;
  }
  if (Math.abs(currentScroll - lastScrollPosition.value) < 50) return;
  showNavBar.value = currentScroll < lastScrollPosition.value;
  lastScrollPosition.value = currentScroll;
}

// Lifecycle hooks
onMounted(() => {
  log("NavigationSecondary mounted()");
  useEventListener(window, "scroll", onScroll);
  if (!route.params?.year) return;
  const pages = pageCache(route.params.year);
  if (!pages) return;
  Object.assign(currentYearPages, pages);
  updateSelectionState();
});

// Watch for route changes
watch(
  () => route.params.year,
  (year) => {
    if (!year) {
      log("No year in route: SecondaryNavigation should bug out");
    } else {
      const pages = pageCache(year);
      if (pages) {
        Object.keys(currentYearPages).forEach((key) => delete currentYearPages[key]);
        Object.assign(currentYearPages, pages);
        updateSelectionState();
      }
    }
  },
  {immediate: true}
);
</script>

<style scoped>
/* CSS limited to this component only */
nav {
  letter-spacing: 0.1px;
  font-weight: 700;
  /* padding: 8px 8px 8px 8px; */
  width: 100%;
  box-sizing: border-box;
  /*overflow: auto; /* prevents main content from wrapping with year */
  position: sticky;
  top: var(--cta-height, 0px); /* below the banner, not under it */
  z-index: 10;
  transform: translate3d(0, 0, 0);
  transition: 0.3s all ease-out;
  background-color: var(--background-color);
}
nav.hidden {
  /* box-shadow: none; */
  transform: translate3d(0, -100%, 0);
}
h2 {
  float: left;
  margin-top: 0px;
  margin-bottom: 8px;
  box-sizing: border-box;

  /* padding-bottom: 30px; */
}
h2 a {
  box-sizing: border-box;
  font-size: 44px;
  line-height: 30px;
}
/* midway between the page panel's top edge (main's, 10px below the header) and what follows,
   by the text drawn (Seán, 2026-10-23): 29px each way wide. Part of the space above is padding
   (--stuckGap, margin less by as much), so it keeps about half the space above it once stuck
   (Seán, 2026-10-29) */
nav {
  --stuckGap: 18px;
  margin-top: calc(1px - var(--stuckGap));
  margin-bottom: 19px;
  padding: var(--stuckGap) 0 0;
  display: flex; /* the year | the wrapping pages */
}
.pages {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  column-gap: 1em; /* between pages on a line */
  padding-left: 1em; /* the year's divider → the first page on each line */
}
a {
  /* border-left: 1px solid #ccc; */
  display: inline-block;
  margin-bottom: 4px;
}
a.year {
  /* the year ('2026' etc): its divider runs the height of all the page lines */
  flex: none;
  border-right: 1px solid var(--text-color-subtle);
  /* border: none; */
  padding-left: 0px;
  margin-left: 0px;
  margin-right: 0px;
}
a {
  color: var(--text-color-subtle); /* c0c0c0*/
  /* text-transform: uppercase; */
  text-decoration: none;
}
a:hover {
  cursor: pointer;
  color: var(--text-color-less-subtle);
}
a {
  vertical-align: top;
}
/* sets the continent color on selection in the navigation list */
a.selected {
  color: var(--brand-color);
}
a.selected:hover {
  color: var(--brand-color); /* the current page: a click goes nowhere new (DESIGN.md § 15) */
}
/* Mobile first - narrow */
.narrow a {
  font-size: 12px; /* was: 0.8rem 18px */
  line-height: 14px; /* was: 1rem  */
}
.narrow a.year {
  font-size: 16px; /*was: 2.5rem 44px */
  line-height: 18px; /* its top level with the first row's (Seán, 2026-10-23; was 32px) */
  padding-right: 6px;
}
/* medium */
.medium a {
  font-size: 15px; /* was: 0.8rem 18px */
  line-height: 21px; /* was: 1rem  */
}
.medium a.year {
  font-size: 25px; /* was: 2.5rem 44px */
  line-height: 29px; /* its top level with the first row's (was 50px) */
  padding-right: 8px;
}
/* wide */
.wide a {
  font-size: 17px; /* was: 0.8rem 18px */
  line-height: 22px; /* was: 1rem  */
}
.wide a.year {
  font-size: 30px; /* was: 2.5rem 44px */
  line-height: 33px; /* its top level with the first row's (was 30px) */
  padding-right: 11px;
}
/* unlimited */
.unlimited a {
  font-size: 18px; /* was: 0.8rem 18px */
  line-height: 24px; /* was: 1rem  */
}
.unlimited a.year {
  font-size: 34px; /* was: 2.5rem 44px */
  line-height: 37px; /* its top level with the first row's (was 34px) */
  padding-right: 12px;
}
/* a phone: the same, however many lines it wraps to (Seán, 2026-10-22/23): 17px each way from
   the panel's edge to the divider and from it to the segmented control (main's 24px top
   padding, less 7; 13 below with the links' own 4px) */
.narrow nav {
  --stuckGap: 14px;
  margin-top: calc(-7px - var(--stuckGap));
  margin-bottom: 13px;
}
</style>
